import { useContext, useState } from "react"
import { Div, Col, Row, Text } from "atomize"
import { toast } from "react-toastify"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import InviteCredentials from "../school/InviteCredentials"
import { RadioBlock, ROLE_HINTS, StepTransition } from "../school/SheetBlocks"
import { theme } from "../settings/config"
import { callFunction } from "../services/firebase.service"
import { schoolContext } from "../providers/school.provider"
import { profileContext } from "../providers/profile.provider"
import { ownsSchool, ROLE_LABELS } from "../utils/school.utils"

const ROLE_OPTIONS = ["colleague", "viewer", "manager"].map(value => ({
	value,
	title: ROLE_LABELS[value],
	hint: ROLE_HINTS[value],
}))

const TEACH_OPTIONS = [
	{ value: true, title: "Sì, insegna", hint: "Ha una giornata sua e si sceglie per le lezioni" },
	{
		value: false,
		title: "No, non insegna",
		hint: "Un direttore, la segreteria: guarda le lezioni, ma non è tra i maestri",
	},
]

// Far entrare qualcuno non vuol dire creargli un account: si apre un
// ingresso al circolo e si consegna il codice. Chi lo usa si è registrato
// da sé, quindi la sua mail e la sua password non passano mai da qui.
//
// Una domanda alla volta: prima che ruolo avrà, poi se insegna. Prima era
// tutto in una pagina — due pastiglie e un paragrafo che spiegava le due —
// e si leggeva più di quanto si scegliesse. Il ruolo lo sceglie solo il
// proprietario; un gestore fa entrare colleghi, e per lui la domanda è una.
export function SchoolInviteModal({ closeModal }) {
	const { selectedSchool, getSelectedSchool } = useContext(schoolContext)
	const { coach } = useContext(profileContext)
	const isOwner = ownsSchool(coach, getSelectedSchool())

	const [step, setStep] = useState(isOwner ? "role" : "teach")
	// da che parte entra la domanda nuova: 1 avanti, -1 indietro, 0 all'apertura
	const [direction, setDirection] = useState(0)
	const goTo = (next, towards) => {
		setDirection(towards)
		setStep(next)
	}
	const [role, setRole] = useState("colleague")
	// chi non scende in campo — un direttore, la segreteria — entra già così
	const [teaches, setTeaches] = useState(true)
	const [isSaving, setIsSaving] = useState(false)
	const [invite, setInvite] = useState(null)

	const schoolName = getSelectedSchool()?.schoolName ?? "questo circolo"

	const handleCreate = async () => {
		setIsSaving(true)
		try {
			const create = callFunction("createSchoolInvite")
			const { data } = await create({
				schoolID: selectedSchool,
				role: isOwner ? role : "colleague",
				teaches,
			})
			setDirection(1)
			setInvite(data)
		} catch (err) {
			toast.error(err.message || "Non sono riuscito ad aprire l'ingresso", {
				position: "top-center",
			})
		} finally {
			setIsSaving(false)
		}
	}

	const buttons = (back, next) => (
		<Div m={{ x: "1rem" }}>
			<Row m={{ t: "1.5rem" }}>
				<Col p="0">
					<SecondaryButton
						m="auto"
						textColor={theme.colors.gunmetal}
						text={isSaving ? "Un attimo..." : "Indietro"}
						textSize="subheader"
						disabled={isSaving}
						action={back}
					/>
				</Col>
				{!isSaving && (
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.primary}
							textColor="white"
							textSize="subheader"
							text={next.text}
							action={next.action}
						/>
					</Col>
				)}
			</Row>
		</Div>
	)

	if (invite) {
		return (
			<Container>
				<StepTransition stepKey="code" direction={direction}>
					<Div m={{ b: "0.5rem" }} textWeight="bold" textSize="title">
						Ecco il codice
					</Div>
					<Text textColor={theme.colors.gunmetal} m={{ b: "1rem" }}>
						Consegnalo a chi vuoi far entrare. Dopo non si può più rileggere: se lo
						perde ne generi un altro, e quello vecchio smette di valere.
					</Text>

					<InviteCredentials invite={invite} schoolName={schoolName} />
				</StepTransition>

				<Div m={{ x: "1rem" }}>
					<Row m={{ t: "1rem" }}>
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={theme.colors.primary}
								textColor="white"
								textSize="subheader"
								text="Fatto"
								action={closeModal}
							/>
						</Col>
					</Row>
				</Div>
			</Container>
		)
	}

	// Due tacche, una per domanda, piena quella a cui si è arrivati. Il
	// gestore ha una domanda sola, e le tacche non dicono niente.
	const progress = current =>
		isOwner && (
			<Div d="flex" m={{ b: "0.9rem" }} style={{ gap: "0.4rem" }} aria-hidden="true">
				{[1, 2].map(n => (
					<Div
						key={n}
						w="1.75rem"
						h="4px"
						rounded="2px"
						bg={n <= current ? theme.colors.primary : theme.colors.gray_light}
					/>
				))}
			</Div>
		)

	const heading = (current, title, hint) => (
		<>
			{progress(current)}
			<Text textSize="caption" textColor={theme.colors.text_light}>
				{isOwner ? `Fai entrare un collega · ${current} di 2` : "Fai entrare un collega"}
			</Text>
			<Div textWeight="bold" textSize="title" m={{ t: "0.25rem" }}>
				{title}
			</Div>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.25rem" }}>
				{hint}
			</Text>
		</>
	)

	if (step === "role") {
		return (
			<Container>
				<StepTransition stepKey="role" direction={direction}>
					{heading(1, "Che ruolo avrà?", "Lo cambi quando vuoi, dalla sua scheda.")}
					<RadioBlock
						m={{ t: "1.25rem" }}
						options={ROLE_OPTIONS}
						selected={role}
						onSelect={setRole}
					/>
				</StepTransition>
				{buttons(closeModal, { text: "Avanti", action: () => goTo("teach", 1) })}
			</Container>
		)
	}

	return (
		<Container>
			<StepTransition stepKey="teach" direction={direction}>
				{heading(
					2,
					"Insegna nel circolo?",
					"Chi non scende in campo resta fuori dall'agenda e dai maestri.",
				)}
				<RadioBlock
					m={{ t: "1.25rem" }}
					options={TEACH_OPTIONS}
					selected={teaches}
					onSelect={setTeaches}
				/>

				{/* quello che si sta per dare, detto in una riga prima di darlo */}
				<Div
					m={{ t: "1.25rem" }}
					p="0.75rem 0.875rem"
					rounded="15px"
					bg={theme.colors.primary_light}>
					<Text textSize="body" textColor={theme.colors.gunmetal}>
						Entrerà a <b>{schoolName}</b> come{" "}
						<b>{ROLE_LABELS[isOwner ? role : "colleague"].toLowerCase()}</b>
						{teaches ? ", e ci insegnerà." : ", senza insegnare."}
					</Text>
				</Div>

				<Text
					textSize="caption"
					textColor={theme.colors.text_light}
					textAlign="center"
					m={{ t: "1.25rem" }}>
					Vedrà allievi e lezioni del circolo. Il codice vale sette giorni, e farne uno
					nuovo spegne il precedente.
				</Text>
			</StepTransition>

			{buttons(isOwner ? () => goTo("role", -1) : closeModal, {
				text: "Genera il codice",
				action: handleCreate,
			})}
		</Container>
	)
}

export default SchoolInviteModal
